import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Loader2, Plus, Search, Type } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField } from "@/components/ui/text-field";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { type AdminFontRow, type components, getAdminFontStats } from "@listto/shared";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { FontCard } from "./font-card";

export function FontManager() {
  const { t } = useI18n();
  const [search, setSearch] = createSignal("");
  const [isIngesting, setIsIngesting] = createSignal(false);

  const fontsQuery = createApiQuery<AdminFontRow[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "fonts"],
    async () => api.GET("/api/admin/font/listAll", {}),
    () => ({ staleTime: 30_000 }),
  );

  const toggleMutation = createApiMutation(async (vars: { fontId: string; isActive: boolean }) =>
    api.POST("/api/admin/font/toggleActive", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { fontId: string }) =>
    api.POST("/api/admin/font/delete", { body: vars }),
  );

  const ingestMutation = createApiMutation(
    async (vars: {
      family: string;
      category: string;
      weights: string[];
      sortOrder?: number;
      forceReIngest?: boolean;
    }) => api.POST("/api/admin/font/ingest", { body: vars }),
  );

  const filteredFonts = createMemo(() => {
    const s = search().toLowerCase();
    const data = fontsQuery.data ?? [];
    return data.filter(
      (f) => f.family.toLowerCase().includes(s) || f.name.toLowerCase().includes(s),
    );
  });

  const stats = createMemo(() => getAdminFontStats(fontsQuery.data ?? []));

  const handleIngest = async (e: Event) => {
    e.preventDefault();
    const family = search().trim();
    if (!family) {
      return;
    }

    setIsIngesting(true);
    try {
      await ingestMutation.mutateAsync({
        family,
        category: "sans-serif" as const,
        weights: ["400"] as const,
      });
      await fontsQuery.refetch();
      setSearch("");
      showToast(t(msg`Font "${family}" ingested successfully`), "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      showToast(t(msg`Error: ${message}`), "error");
    } finally {
      setIsIngesting(false);
    }
  };

  return (
    <div class="space-y-8">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <Type class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Font Catalog`)}</h2>
            <p class="text-sm text-muted-foreground">
              {(() => {
                const total = stats().total;
                const active = stats().active;
                return t(msg`${total} fonts total • ${active} active`);
              })()}
            </p>
          </div>
        </div>
      </div>

      <Card class="bg-primary/5 border-primary/10">
        <CardContent class="p-4">
          <form onSubmit={handleIngest} class="flex gap-2">
            <div class="relative flex-1">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <TextField
                placeholder={t(msg`Google font name (e.g., Roboto, Inter...)`)}
                class="pl-9"
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
              />
            </div>
            <Button type="submit" disabled={isIngesting() || !search()}>
              <Show
                when={isIngesting()}
                fallback={
                  <>
                    <Plus class="mr-2 h-4 w-4" /> {t(msg`Ingest`)}
                  </>
                }
              >
                <Loader2 class="mr-2 h-4 w-4 animate-spin" /> {t(msg`Ingesting...`)}
              </Show>
            </Button>
          </form>
        </CardContent>
      </Card>

      <Show
        when={!fontsQuery.isLoading}
        fallback={
          <div class="grid gap-4 md:grid-cols-2">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-32 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="grid gap-4 md:grid-cols-2">
          <For each={filteredFonts()}>
            {(font) => (
              <FontCard
                font={font}
                onToggleActive={(id, active) => {
                  toggleMutation.mutate(
                    { fontId: id, isActive: active },
                    {
                      onSuccess: () => {
                        void fontsQuery.refetch();
                      },
                    },
                  );
                }}
                onDeleteConfirm={(id) => {
                  deleteMutation.mutate(
                    { fontId: id },
                    {
                      onSuccess: () => {
                        void fontsQuery.refetch();
                        const family = font.family;
                        showToast(t(msg`Font "${family}" deleted`), "success");
                      },
                    },
                  );
                }}
                t={t}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
